import {
  compareAsc,
  differenceInHours,
  format,
  setHours,
  setMinutes,
  startOfDay,
  subHours,
} from "date-fns";
import {
  hoursMinutesFormat,
  leadingZerosDateFormat,
} from "../../common/helpers/formatDateTime";
import { ShiftDetailsType } from "./additionalInfo/types";
import {
  OrderEditTargetingRule,
  ShiftDetailDTO,
  ShiftDetails2,
} from "./orderAPI";
import { AutomatchingStatus, OrderFields, OrderRow } from "./orderTypes";
import { isAutomatchingDelayTooLong } from "./automatchingDelayValidation";
import { TalentPool } from "./types/common";
import { parseToInterval } from "./OrderTimeField";
import { OFFER_FIRST_TO_OWN } from "./constants";
import { getBundleRowValues } from "./newOrder/short/orderFormShortRowHandlers";
import { SliceState } from "../localized/localizedApi";

export const isBundleOrder = (shiftDetails: ShiftDetails2[]): boolean =>
  new Set(shiftDetails.map((shift) => shift.orderRowId)).size > 1;

export const formatShiftTimeRange = (start: number, end: number): string =>
  `${format(new Date(start), hoursMinutesFormat)} - ${format(
    new Date(end),
    hoursMinutesFormat,
  )}`;

export const formatShiftSummaryLabel = (
  start: number,
  end: number,
  locationName: string,
): string => {
  const date = format(new Date(start), leadingZerosDateFormat);
  const time = formatShiftTimeRange(start, end);
  return locationName ? `${date} ${time} - ${locationName}` : `${date} ${time}`;
};

export const getCustomerCostCenterName = (
  costCenters: Array<{ id: string; name: string }>,
  customerCostCenterId?: string,
): string =>
  costCenters.find((cc) => cc.id === customerCostCenterId)?.name || "";

export const sortOrderFieldsByDateTime = (a: OrderFields, b: OrderFields) => {
  if (a.date && b.date) {
    const startOfDateA = startOfDay(a.date);
    const startOfDateB = startOfDay(b.date);

    // compare dates
    const dateComparison = compareAsc(startOfDateA, startOfDateB);
    if (dateComparison !== 0) return dateComparison;
  }

  // same dates, compare times
  if (a.time && b.time) {
    const startTimeA = parseInt(a.time.substring(0, 4), 10);
    const startTimeB = parseInt(b.time.substring(0, 4), 10);
    return startTimeA - startTimeB;
  }

  return 0;
};

export const orderFieldTimePattern = (value: string): RegExp[] | null => {
  if (value.length < 8) {
    return null; // Return null if value is not at least 8 digits
  }

  const firstDigit = value[0];
  const fifthDigit = value[4];
  const startTimeStartsWithTwo = firstDigit === "2";
  const endTimeStartsWithTwo = fifthDigit === "2";
  return [
    /[0-2]/g,
    startTimeStartsWithTwo ? /[0-3]/g : /[0-9]/g,
    /[0-5]/g,
    /[0-9]/g,
    /[0-2]/g,
    endTimeStartsWithTwo ? /[0-3]/g : /[0-9]/g,
    /[0-5]/g,
    /[0-9]/g,
  ];
};

export const flexibleStartTimePattern = (value: string): RegExp[] | null => {
  if (value.length !== 4) {
    return null; // Return null if value is not exactly 4 digits
  }

  const firstDigit = value[0];
  const startsWithTwo = firstDigit === "2";
  return [/[0-2]/g, startsWithTwo ? /[0-3]/g : /[0-9]/g, /[0-5]/g, /[0-9]/g];
};

// Helper function to convert HHMM string to minutes for comparison
export const timeStringToMinutes = (timeString: string): number => {
  if (timeString.length !== 4) return -1;
  const hours = parseInt(timeString.substring(0, 2), 10);
  const minutes = parseInt(timeString.substring(2, 4), 10);
  return hours * 60 + minutes;
};

// Validate flexible start time against shift start and end times
export const validateFlexibleStartTime = (
  flexibleStartTime: string,
  shiftTime: string,
  isRequired: boolean = false,
): { isValid: boolean; errorType?: string } => {
  // First check if flexible start time has valid format
  if (!flexibleStartTime) {
    return isRequired
      ? { isValid: false, errorType: "required" }
      : { isValid: true };
  }

  if (flexibleStartTime.length !== 4) {
    return { isValid: false, errorType: "invalidTime" };
  }

  // Check exact length and format
  const timePattern = flexibleStartTimePattern(flexibleStartTime);
  const isValidFormat =
    flexibleStartTime.length === 4 &&
    timePattern &&
    flexibleStartTime
      .split("")
      .every((char, index) => char.match(timePattern[index]));

  if (!isValidFormat) {
    return { isValid: false, errorType: "invalidTime" };
  }

  // Check if time values are within valid ranges
  const hours = parseInt(flexibleStartTime.substring(0, 2), 10);
  const minutes = parseInt(flexibleStartTime.substring(2, 4), 10);

  if (hours > 23 || minutes > 59) {
    return { isValid: false, errorType: "invalidTime" };
  }

  // Check if shift time is valid for comparison
  if (!shiftTime || shiftTime.length !== 8) {
    return { isValid: true }; // Can't validate against invalid shift time
  }

  const shiftStartTime = shiftTime.substring(0, 4);
  const shiftEndTime = shiftTime.substring(4, 8);

  const flexibleMinutes = timeStringToMinutes(flexibleStartTime);
  const shiftStartMinutes = timeStringToMinutes(shiftStartTime);
  const shiftEndMinutes = timeStringToMinutes(shiftEndTime);

  // Handle overnight shifts (when end time is before start time)
  const isOvernightShift = shiftEndMinutes < shiftStartMinutes;

  if (isOvernightShift) {
    // For overnight shifts, flexible start can be:
    // 1. Between start time and 24:00 (same day)
    // 2. Between 00:00 and end time (next day)
    const isValidSameDay = flexibleMinutes > shiftStartMinutes;
    const isValidNextDay = flexibleMinutes < shiftEndMinutes;

    if (!isValidSameDay && !isValidNextDay) {
      return { isValid: false, errorType: "outsideShiftRange" };
    }
  } else {
    // For regular shifts, flexible start must be between start and end
    if (flexibleMinutes <= shiftStartMinutes) {
      return { isValid: false, errorType: "beforeShiftStart" };
    }
    if (flexibleMinutes >= shiftEndMinutes) {
      return { isValid: false, errorType: "afterShiftEnd" };
    }
  }

  return { isValid: true };
};

export const formatFlexibleStartTime = (
  shiftDetails: ShiftDetailDTO | ShiftDetails2,
  t: Function,
  withLabel = true,
) => {
  let timeString = "";
  if ("shiftDetail" in shiftDetails) {
    // ShiftDetails2
    timeString = `${format(new Date(shiftDetails.shiftDetail.time.start), hoursMinutesFormat)} - ${format(new Date(shiftDetails.shiftDetail.flexibleStartUntil as number), hoursMinutesFormat)}`;
  } else {
    timeString = `${format(new Date(shiftDetails.start), hoursMinutesFormat)} - ${format(new Date(shiftDetails.flexibleStartUntil as number), hoursMinutesFormat)}`;
  }

  return withLabel
    ? `${t("order:flexibleShiftLabel")}: ${t("order:startTime")} ${t("common:at")} ${timeString}`
    : timeString;
};

// Find the earliest shift time from shift details array
export const getEarliestShiftTime = (
  shiftDetails: Array<ShiftDetailsType | ShiftDetails2>,
): number | null => {
  if (!shiftDetails.length) {
    return null;
  }

  const earliestShift = shiftDetails.reduce((earliest, current) => {
    if (isShiftDetailsType(current)) {
      // ShiftDetailsType / new order - date + time
      if (!current.date) return earliest;
      const currentTime = createShiftDateTime(current.date, current.time);

      if (isShiftDetailsType(earliest)) {
        if (!earliest.date) return current;
        const earliestTime = createShiftDateTime(earliest.date, earliest.time);
        return currentTime < earliestTime ? current : earliest;
      } else {
        // ShiftDetails2 / edit order - shiftDetail.time.start
        const earliestTime = earliest.shiftDetail.time.start;
        return currentTime < earliestTime ? current : earliest;
      }
    } else {
      const currentTime = current.shiftDetail.time.start;

      if (isShiftDetailsType(earliest)) {
        if (!earliest.date) return current;
        const earliestTime = createShiftDateTime(earliest.date, earliest.time);
        return currentTime < earliestTime ? current : earliest;
      } else {
        const earliestTime = earliest.shiftDetail.time.start;
        return currentTime < earliestTime ? current : earliest;
      }
    }
  });

  if (isShiftDetailsType(earliestShift)) {
    if (!earliestShift.date) return null;
    return createShiftDateTime(earliestShift.date, earliestShift.time);
  } else {
    return earliestShift.shiftDetail.time.start;
  }
};

export const sortShiftRowsByTalentAndStart = (
  rows: ShiftDetails2[],
): ShiftDetails2[] => {
  const firstIndexByTalent = new Map<string, number>();
  const indexedRows = rows.map((row, index) => {
    const talentId = row.assignedTalent?.id;
    if (talentId && !firstIndexByTalent.has(talentId)) {
      firstIndexByTalent.set(talentId, index);
    }
    return { row, index };
  });

  return indexedRows
    .sort((a, b) => {
      const aTalentId = a.row.assignedTalent?.id;
      const bTalentId = b.row.assignedTalent?.id;
      if (aTalentId && !bTalentId) return -1;
      if (!aTalentId && bTalentId) return 1;
      if (aTalentId && bTalentId) {
        const aGroup = firstIndexByTalent.get(aTalentId) ?? a.index;
        const bGroup = firstIndexByTalent.get(bTalentId) ?? b.index;
        if (aGroup !== bGroup) return aGroup - bGroup;
        const aStart = a.row.shiftDetail.time.start;
        const bStart = b.row.shiftDetail.time.start;
        if (aStart !== bStart) return aStart - bStart;
      }
      return a.index - b.index;
    })
    .map(({ row }) => row);
};

export const hasCancelledShiftByTalent = (
  rows: ShiftDetails2[],
): Map<string, boolean> => {
  const cancelledByTalent = new Map<string, boolean>();
  rows.forEach((row) => {
    const talentId = row.assignedTalent?.id;
    if (
      talentId &&
      (row.assignedTalent?.reasonForCancellation || row.assignedTalent?.absent)
    ) {
      cancelledByTalent.set(talentId, true);
    }
  });
  return cancelledByTalent;
};

// Helper function to create a proper shift date-time
const createShiftDateTime = (date: Date, time: string): number => {
  const hours = parseInt(time.substring(0, 2), 10);
  const minutes = parseInt(time.substring(2, 4), 10);

  const baseDate = startOfDay(date);

  // Set the specific hours and minutes
  const shiftDateTime = setMinutes(setHours(baseDate, hours), minutes);

  return shiftDateTime.getTime();
};

// Validate notification time against earliest shift
export const validateNotificationTime = (
  hours: number,
  shiftDetails: Array<ShiftDetailsType | ShiftDetails2>,
): { isValid: boolean; errorType?: string; maxAllowedHours?: number } => {
  const earliestShiftTime = getEarliestShiftTime(shiftDetails);
  if (!earliestShiftTime) {
    return { isValid: true }; // No shift details, can't validate
  }

  const now = new Date();
  const earliestShift = new Date(earliestShiftTime);
  const notificationTime = subHours(earliestShift, hours);
  const maxAllowedHours = differenceInHours(earliestShift, now);

  if (notificationTime <= now) {
    return {
      isValid: false,
      errorType: "notificationTimePassed",
      maxAllowedHours: Math.max(0, maxAllowedHours),
    };
  }

  return { isValid: true };
};

const isShiftDetailsType = (
  shiftDetails: ShiftDetailsType | ShiftDetails2,
): shiftDetails is ShiftDetailsType => "date" in shiftDetails;

// Find earliest shift for new short order
export const getEarliestShiftStart = (
  orderRows: OrderRow[],
  bundleId: string | undefined,
  values: OrderFields,
): Date | null => {
  if (bundleId) {
    const bundleValues = getBundleRowValues(orderRows, bundleId) || [];
    const starts = bundleValues.map((bundle) => {
      const startDate = new Date(bundle.date as Date);
      const { startDateTime } = parseToInterval(bundle.time, startDate);
      return new Date(startDateTime);
    });
    if (starts.length === 0) {
      return null;
    }
    return starts.reduce((earliest, current) =>
      current.getTime() < earliest.getTime() ? current : earliest,
    );
  }

  if (!values.date || !values.time) {
    return null;
  }
  const startDate = new Date(values.date as Date);
  const { startDateTime } = parseToInterval(values.time, startDate);
  return new Date(startDateTime);
};

export const automatchingIsNotInThePast = (
  firstShiftStart: Date | null,
): boolean => {
  if (!firstShiftStart) {
    return true;
  }
  const now = new Date();
  return firstShiftStart.getTime() > now.getTime();
};

export const shouldValidateDelay = (
  selectedPool: TalentPool | null,
  poolOfferTarget: string | null,
  automatchingStatus: AutomatchingStatus,
  isAutomatchInThePast: boolean,
): boolean => {
  if (isAutomatchInThePast) {
    return false;
  }
  if (selectedPool === TalentPool.WORK_LOCATION) {
    return poolOfferTarget === OFFER_FIRST_TO_OWN;
  }
  return automatchingStatus !== AutomatchingStatus.Off;
};

export const checkIsDelayTooLong = (
  delayMinutes: number | null,
  firstShiftStart: Date | null,
  shouldValidateDelay: boolean,
) => {
  if (!shouldValidateDelay || !delayMinutes) {
    return false;
  }
  if (
    isAutomatchingDelayTooLong({
      delayMinutes,
      firstShiftStart,
    })
  ) {
    return true;
  }
  return false;
};

export const toFormFieldRules = (
  rules: OrderEditTargetingRule[],
  localized: SliceState,
) => {
  return rules.map((rule, i) => ({
    customerCompany: rule.custCompany,
    customerOrganization: rule.custOrgs ? rule.custOrgs[0] : null,
    workLocations: rule.workLocs,
    workTitles: rule.workTitles.map((id) => ({
      id,
      name: localized.workTitle[id],
    })),
    isDefaultRule: i === 0,
  }));
};
